import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Grid,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core'
import {
  BoreholeFieldDefinitions,
  BoreholeFieldDefinitionsProps,
} from './BoreholeFieldDefinitions'
import { BoreholeFieldInherentNumbers } from './BoreholeFieldInherentNumbers'
import { BoreholeFieldUsageTableProps } from './BoreholeFieldUsageTable'
import { formatDate } from '../../common/util/date-util'
import { BoreholeFieldConfirmationInput } from '../../../generated/graphql'
import { isConfirmationValid } from '../../../borehole-field-container/borehole-field-confirmation-validator'
import { createConfirmationData } from '../../../borehole-field-container/borehole-field-data-creator'

const spacing = 4

const useStyles = makeStyles((theme) => ({
  subtitle: {
    color: theme.palette.grey[900],
    textTransform: 'none',
  },

  successContainer: {
    color: 'white',
    backgroundColor: `${theme.palette.success.main} !important`,
    '&:hover': {
      backgroundColor: theme.palette.success.main,
      filter: 'brightness(120%)',
    },
  },

  warningContainer: {
    color: 'white',
    backgroundColor: `${theme.palette.warning.main} !important`,
    '&:hover': {
      backgroundColor: theme.palette.warning.main,
      filter: 'brightness(120%)',
    },
  },

  disclaimer: {
    color: theme.palette.grey[700],
    marginTop: 'auto',
  },
}))

type Props = BoreholeFieldDefinitionsProps & BoreholeFieldUsageTableProps

export function BoreholeFieldConfirmation({
  container,
  updateContainer,
  harvested,
  wasteInput,
}: Props): React.ReactElement {
  const classes = useStyles()
  const { t } = useTranslation()
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => {
    const currentData = createConfirmationData(container, harvested, wasteInput)
    const isValid = isConfirmationValid(container, currentData)

    setConfirmed(isValid)
  }, [container, harvested, wasteInput])

  const { confirmationType } = container

  const confirmationText = container.confirmation
    ? t(`boreholeField.confirmationBasedOn.${container.confirmation?.type}`, {
        name: container.confirmation.confirmer.displayName,
        date: formatDate(
          new Date(container.confirmation.date),
          t('date.short'),
        ),
      })
    : ''

  const onConfirmationClick = async () => {
    if (!confirmationType) {
      throw new Error('Unexpected error while getting confirmation type')
    }

    const confirmation: BoreholeFieldConfirmationInput = {
      type: confirmationType,
      data: createConfirmationData(container, harvested, wasteInput),
    }

    await updateContainer({ confirmation })
  }

  return (
    <Paper>
      <Box p={2}>
        <Grid container spacing={spacing}>
          <Grid item xs={12} sm={6}>
            <BoreholeFieldDefinitions
              container={container}
              updateContainer={updateContainer}
            />
            <Box pt={4} />
            <BoreholeFieldInherentNumbers
              container={container}
              harvested={harvested}
              wasteInput={wasteInput}
            />
          </Grid>
          <Grid item xs={6}>
            <Box
              textAlign="center"
              justifyContent="center"
              display="flex"
              flexDirection="column"
              height="100%"
            >
              <Box
                height="60%"
                display="flex"
                flexDirection="column"
                justifyContent="flex-end"
              >
                <Typography variant="subtitle1" className={classes.subtitle}>
                  {confirmationText}
                </Typography>
                <Box p={2}>
                  <Button
                    color="default"
                    variant="contained"
                    onClick={onConfirmationClick}
                    className={
                      confirmed
                        ? classes.successContainer
                        : classes.warningContainer
                    }
                  >
                    {t(`boreholeField.confirmBasedOn.${confirmationType}`)}
                  </Button>
                </Box>
              </Box>

              <Box
                p={2}
                display="flex"
                flexDirection="column"
                textAlign="left"
                height="40%"
              >
                <div className={classes.disclaimer}>{`* ${t(
                  'boreholeField.inherentValueDisclaimer',
                )}`}</div>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={3} />
        </Grid>
      </Box>
    </Paper>
  )
}
